import React, { useEffect } from 'react';
import { X, Loader, Upload, Download, Clock, Activity, NetworkIcon } from "lucide-react";
import { useDispatch, useSelector } from 'react-redux';
import { fetchTestDetails } from '../../store/actions';

const SummaryElement = ({ title, details = '-' }) => {
  return (
    <div className="bg-white rounded-md p-3 border border-gray-200">
      <p className="text-sm font-medium text-gray-500">{title}</p>
      <p className="mt-1">{details}</p>
    </div>
  );
}

const TestSlideout = ({ testId, isOpen, onClose }) => {
  const dispatch = useDispatch();

  const { data: testData, loading } = useSelector((state) => state.TestDetails);
  const { userData: user } = useSelector(state => state.UserStatus);
  const isOnline = user?.isOnline;

  useEffect(() => {
    if (!testId) { return; }

    dispatch(fetchTestDetails(testId));
  }, [testId]);

  const getFullName = () => {
    if (!user?.first_name || !user?.last_name) {
      return 'Public User';
    }

    return `${user.first_name} ${user.last_name}`;
  }

  return (
    <div className={`fixed top-[40px] bottom-0 right-0 w-full md:w-2/3 lg:w-1/2 xl:w-2/5 bg-white shadow-2xl transform ${isOpen ? 'translate-x-0' : 'translate-x-full'} transition-transform duration-300 ease-in-out overflow-y-auto z-30`}>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Test Results</h2>
              <p className="text-sm text-gray-500">ID: {testId}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">            
            <button
              onClick={onClose}
              className="inline-flex items-center p-2 border border-transparent rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none"
            >
              <X className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 px-6 py-4 space-y-6">
          {/* User Information */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">{getFullName()}</h3>
                <p className="text-gray-600">{user?.email}</p>
              </div>
              <div className="flex items-center">
                <div className={`h-2.5 w-2.5 rounded-full mr-1.5 ${isOnline ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                <span className="text-sm font-medium">{isOnline ? 'Online' : 'Offline'}</span>
              </div>
            </div>
          </div>

          {/* Summary Info */}
          <div>
            <h3 className="flex gap-2 text-lg font-medium text-gray-900 mb-2">
              {loading && (<Loader size={24} className="animate-spin"/>)}
              Summary
            </h3>
            {!loading && (<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SummaryElement title="Session Date" details={testData?.session_date}/>
              <SummaryElement title="IP Address" details={testData?.ip_address}/>
              <SummaryElement title="Location" details={testData?.location}/>
              <SummaryElement title="Service Provider" details={testData?.service_provider}/>
              <SummaryElement title="Test Type" details={testData?.test_type}/>
              <SummaryElement title="Test Trigger" details={testData?.trigger_by}/>
            </div>)}
          </div>

          {/* Internet Performance */}
          <div>
            <h3 className="flex gap-2 text-lg font-medium text-gray-900 mb-2">
              {loading && (<Loader size={24} className="animate-spin"/>)}
              Internet Performance
            </h3>
            {!loading && (<div className="grid grid-cols-1 sm:grid-cols-1 gap-4">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-1.5">
                  <Download className="h-4 w-4 text-green-500" />
                  <span className="text-sm font-medium">Download Speed</span>
                </div>
                <span className="text-sm">{testData?.download || '0'} Mbps</span>
              </div>

              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-1.5">
                  <Upload className="h-4 w-4 text-blue-500" />
                  <span className="text-sm font-medium">Upload Speed</span>
                </div>
                <span className="text-sm">{testData?.upload || '0'} Mbps</span>
              </div>

              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4 text-indigo-500" />
                  <span className="text-sm font-medium">Latency</span>
                </div>
                <span className="text-sm">{testData?.latency || '0'} ms</span>
              </div>

              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-1.5">
                  <Activity className="h-4 w-4 text-red-500" />
                  <span className="text-sm font-medium">Packet Loss</span>
                </div>
                <span className="text-sm">{testData?.packetLoss || '0'}</span>
              </div>

              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-1.5">
                  <NetworkIcon className="h-4 w-4 text-amber-500" />
                  <span className="text-sm font-medium">Jitter</span>
                </div>
                <span className="text-sm">{testData?.jitter || '0'} ms</span>
              </div>
            </div>)}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <div className="flex items-center justify-end space-x-3">
            <button
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestSlideout;
